import styled from "@emotion/styled"
import Drawer from "@mui/material/Drawer"

const StyledModernDashboard = styled.div`
  position: relative;
  padding: 22px;
  border-radius: 24px;
  background: radial-gradient(circle at 6% -10%, rgba(99, 102, 241, 0.12) 0%, transparent 34%),
    radial-gradient(circle at 110% 110%, rgba(14, 165, 233, 0.1) 0%, transparent 42%),
    linear-gradient(180deg, #f5f8ff 0%, #eef3fc 100%);

  &.theme-dark {
    background: radial-gradient(circle at 0% 0%, rgba(56, 189, 248, 0.12) 0%, transparent 30%),
      radial-gradient(circle at 100% 100%, rgba(147, 197, 253, 0.12) 0%, transparent 42%),
      linear-gradient(180deg, #0b1220 0%, #111b2e 100%);

    h2 {
      color: #e2e8f0;
    }
  }
`

const StyledFilterCard = styled.div`
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 251, 255, 0.95) 100%);
  backdrop-filter: blur(8px);
  border: 1px solid #e2e8f4;
  border-radius: 18px;
  padding: 18px;
  margin: 14px 0 20px;
  box-shadow: 0 14px 34px rgba(17, 24, 39, 0.08);

  .filter-title {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 12px;
  }

  .filter-row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
  }

  .MuiToggleButtonGroup-root {
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #d9e1ee;
    background: #ffffff;
  }

  .MuiToggleButton-root {
    border: 0;
    text-transform: none;
    font-size: 12px;
    font-weight: 600;
    color: #334155;
    padding: 7px 12px;
  }

  .MuiToggleButton-root.Mui-selected {
    background: linear-gradient(160deg, #e0ecff 0%, #dbeafe 100%);
    color: #1d4ed8;
  }

  .insights-warning {
    margin-top: 10px;
    font-size: 12px;
    color: var(--cms-warning);
    background: var(--cms-warning-bg);
    border: 1px solid var(--cms-border);
    border-radius: 8px;
    padding: 8px 10px;
  }

  &.dark {
    background: linear-gradient(150deg, rgba(15, 23, 42, 0.88) 0%, rgba(30, 41, 59, 0.9) 100%);
    border-color: #32455f;

    .filter-title {
      color: #dbe7fb;
    }

    .MuiToggleButtonGroup-root {
      border-color: #3b4d67;
      background: #1e293b;
    }

    .MuiToggleButton-root {
      color: #d3dded;
      background: transparent;
    }

    .MuiToggleButton-root.Mui-selected {
      background: linear-gradient(160deg, #334155 0%, #1d4ed8 100%);
      color: #f8fafc;
    }
  }
`

const StyledKpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`

const StyledKpiCard = styled.div`
  background: linear-gradient(165deg, #121a2d 0%, #1b2743 65%, #243457 100%);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 18px;
  padding: 18px;
  color: #fff;
  min-height: 122px;
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.24);
  transition: transform 180ms ease, box-shadow 180ms ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 22px 40px rgba(15, 23, 42, 0.28);
  }

  .label {
    font-size: 13px;
    opacity: 0.84;
    margin-bottom: 10px;
    letter-spacing: 0.01em;
  }

  .value {
    font-size: 34px;
    font-weight: 600;
    line-height: 1;
  }

  .meta {
    margin-top: 11px;
    font-size: 12px;
    color: #d3def6;
  }

  .sparkline {
    width: 100%;
    height: 44px;
    margin-top: 10px;
  }

  .trend-up {
    color: #34d399;
  }

  .trend-down {
    color: #fca5a5;
  }

  &.light {
    background: linear-gradient(180deg, #ffffff 0%, #f6faff 100%);
    color: #0f172a;
    border: 1px solid #dbe4f3;
    box-shadow: 0 14px 28px rgba(15, 23, 42, 0.1);
    .label {
      color: #334155;
      opacity: 1;
    }
    .meta {
      color: #475569;
    }
  }
`

const StyledInsightGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 720px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

const StyledInsightPanel = styled.div`
  background: linear-gradient(160deg, #ffffff 0%, #fbfdff 100%);
  border: 1px solid #e5ebf5;
  border-radius: 18px;
  padding: 16px 16px 14px;
  box-shadow: 0 12px 26px rgba(17, 24, 39, 0.08);
  min-height: 190px;
  transition: transform 180ms ease, box-shadow 180ms ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 34px rgba(17, 24, 39, 0.11);
  }

  .panel-title {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #111827;
  }

  .panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .panel-actions {
    display: inline-flex;
    align-items: center;
    gap: 4px;

    button {
      border: 1px solid #dde5f2;
      border-radius: 10px;
      background: #f8fafe;
      width: 30px;
      height: 30px;
    }
  }

  .empty {
    font-size: 13px;
    color: #6b7280;
    margin-top: 10px;
  }

  .secondary {
    color: #64748b;
    font-size: 12px;
    margin-top: 5px;
  }

  .tier-breakdown-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 10px;
  }

  .tier-breakdown-card {
    border: 1px solid #e5ebf5;
    border-radius: 14px;
    background: linear-gradient(180deg, #fbfdff 0%, #f4f8ff 100%);
    padding: 12px;
  }

  .tier-breakdown-card.total {
    border-color: #bfdbfe;
    background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%);
  }

  .tier-label {
    color: #0f172a;
    font-size: 13px;
    font-weight: 700;
    margin-bottom: 10px;
  }

  .tier-count-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    color: #475569;
    font-size: 12px;
    margin-top: 7px;
  }

  .tier-count-row strong {
    color: #111827;
    font-size: 18px;
  }

  &.ai-usage-panel {
    .panel-header {
      align-items: flex-start;
      gap: 14px;
    }

    .accounting-help {
      flex: 0 0 auto;
      color: #2563eb;
      font-size: 12px;
      font-weight: 600;
      cursor: help;
      border-bottom: 1px dotted currentColor;
    }

    .ai-tier-grid {
      grid-template-columns: repeat(4, minmax(190px, 1fr));
      overflow-x: auto;
      padding-bottom: 4px;
    }

    .tier-count-row strong {
      font-size: 14px;
      white-space: nowrap;
    }

    .tier-count-row.cost-row {
      border-top: 1px solid #e2e8f0;
      margin-top: 10px;
      padding-top: 10px;
    }

    .coverage-summary {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 10px;
    }

    .coverage-stat {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      border: 1px solid #dbeafe;
      border-radius: 12px;
      padding: 12px;
      background: #f8fbff;
      color: #475569;
      font-size: 12px;
    }

    .coverage-stat strong {
      color: #0f172a;
      font-size: 20px;
    }

    .coverage-stat.warning {
      border-color: #fde68a;
      background: #fffbeb;
    }

    .coverage-notes {
      margin-top: 12px;
      padding: 10px 12px;
      border-left: 3px solid #60a5fa;
      border-radius: 0 8px 8px 0;
      background: #eff6ff;
      color: #475569;
      font-size: 12px;
      line-height: 1.5;
    }
  }

  &.dark {
    background: linear-gradient(170deg, #0f172a 0%, #172338 100%);
    border: 1px solid #2b3d58;
    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.22);
    .panel-title {
      color: #e2e8f0;
    }
    .empty {
      color: #94a3b8;
    }
    .secondary {
      color: #94a3b8;
    }
    .tier-breakdown-card {
      background: linear-gradient(180deg, #172338 0%, #111827 100%);
      border-color: #334155;
    }
    .tier-breakdown-card.total {
      background: linear-gradient(180deg, #1e3a8a 0%, #172554 100%);
      border-color: #3b82f6;
    }
    .tier-label {
      color: #f8fafc;
    }
    .tier-count-row {
      color: #cbd5e1;
    }
    .tier-count-row strong {
      color: #ffffff;
    }
    &.ai-usage-panel {
      .accounting-help {
        color: #7dd3fc;
      }
      .tier-count-row.cost-row {
        border-top-color: #334155;
      }
      .coverage-stat {
        border-color: #334155;
        background: #111827;
        color: #cbd5e1;
      }
      .coverage-stat strong {
        color: #f8fafc;
      }
      .coverage-stat.warning {
        border-color: #92400e;
        background: #451a03;
      }
      .coverage-notes {
        border-left-color: #38bdf8;
        background: #172554;
        color: #cbd5e1;
      }
    }
    .panel-header .panel-actions button {
      color: #cbd5e1;
      border-color: #3b4b63;
      background: rgba(51, 65, 85, 0.55);
    }
  }

  @media (max-width: 720px) {
    &.ai-usage-panel {
      .panel-header {
        flex-direction: column;
      }
      .ai-tier-grid {
        grid-template-columns: repeat(4, minmax(180px, 1fr));
      }
    }
  }
`

const StyledInsightList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    border: 1px solid var(--cms-border);
    border-radius: 10px;
    padding: 8px 10px;
    margin-bottom: 8px;
    font-size: 13px;
    color: var(--cms-text-primary);
    background: var(--cms-surface-muted);
    display: flex;
    justify-content: space-between;
    gap: 10px;
  }

  li:last-of-type {
    margin-bottom: 0;
  }

  .primary {
    font-weight: 600;
  }

  .secondary {
    color: var(--cms-text-secondary);
    font-size: 12px;
  }
`

const StyledMessageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .message-item {
    border: 1px solid var(--cms-border);
    border-radius: 12px;
    padding: 10px 11px;
    background: var(--cms-surface-muted);
  }

  .agent {
    font-size: 12px;
    font-weight: 700;
    color: var(--cms-text-primary);
    margin-bottom: 6px;
  }

  .question {
    font-size: 12px;
    color: var(--cms-text-primary);
    margin-bottom: 4px;
  }

  .response {
    font-size: 12px;
    color: var(--cms-text-secondary);
  }
`

const StyledAgentTrendChart = styled.div`
  width: 100%;
  height: 220px;
`

const StyledHeatmap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .heatmap-row {
    display: grid;
    grid-template-columns: 90px repeat(8, minmax(26px, 1fr));
    gap: 4px;
    align-items: center;
  }

  .row-label {
    font-size: 11px;
    color: var(--cms-text-secondary);
    font-weight: 600;
  }

  .week-label {
    text-align: center;
    font-size: 10px;
    color: var(--cms-text-secondary);
  }

  .cell {
    border-radius: 5px;
    min-height: 24px;
    border: 1px solid var(--cms-border);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    color: var(--cms-text-primary);
  }
`

const StyledDashboard = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 14px;
  padding: 0 22px 22px;
`

const StyledInfoCard = styled.div`
  position: relative;
  display: flex;
  background: var(--cms-card-bg);
  color: var(--cms-text-primary);
  transition: transform 180ms ease, box-shadow 200ms ease;
  border-radius: 16px;
  border: 1px solid var(--cms-border);
  overflow: hidden;
  width: 100%;
  min-height: 140px;
  padding: 14px 16px;
  flex-direction: column;
  box-shadow: var(--cms-shadow);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 28px rgba(15, 23, 42, 0.12);
  }

  h3 {
    margin: 2px 0 10px;
    font-size: 14px;
    font-weight: 700;
    min-height: 32px;
    color: var(--cms-text-primary);
  }
  .value {
    font-size: 32px;
    font-weight: 600;
    line-height: 1.1;
    &.text {
      font-size: 14px;
      color: var(--cms-text-secondary);
      font-weight: 500;
    }
    &.info {
      font-size: 13px;
      color: var(--cms-text-primary);
      font-weight: 500;
      line-height: 1.45;
    }
  }
  .icon-btn {
    position: absolute;
    top: 8px;
    right: 7px;
  }

  .icon-btn button {
    border-radius: 10px;
    border: 1px solid var(--cms-border);
    background: var(--cms-surface-muted);
  }
`

const StyledInfoSearchBar = styled.div`
  display: flex;
  background-color: transparent;
  margin: 0;
  -webkit-transition: box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  transition: box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  border-radius: 8px;
  padding: 0;

  .time-input {
    margin-right: 10px;
  }
`
const StyledDashboardHeading = styled.h2`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: var(--cms-text-primary);
`

const StyledLessonEngagementChart = styled.div`
  width: 100%;
  height: min(340px, 52vh);
  margin-top: 8px;
`

const StyledEngagementKpiStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(148px, 1fr));
  gap: 10px;
  margin: 4px 0 6px;

  .engagement-kpi {
    border-radius: 12px;
    padding: 10px 12px;
    background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
    border: 1px solid #e2e8f0;
    font-size: 12px;
    color: #475569;
  }

  .engagement-kpi .kpi-label {
    font-weight: 600;
    color: #64748b;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 10px;
  }

  .engagement-kpi .kpi-value {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.1;
  }

  .engagement-kpi .kpi-hint {
    margin-top: 4px;
    font-size: 11px;
    color: #94a3b8;
  }

  &.dark .engagement-kpi {
    background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
    border-color: #334155;
    color: #cbd5e1;
  }

  &.dark .engagement-kpi .kpi-label {
    color: #94a3b8;
  }

  &.dark .engagement-kpi .kpi-value {
    color: #f8fafc;
  }

  &.dark .engagement-kpi .kpi-hint {
    color: #64748b;
  }
`

const StyledEngagementSearch = styled.div`
  margin: 6px 0 10px;

  .MuiTextField-root {
    width: 100%;
    max-width: 420px;
  }
`

const StyledChartDrawer = styled(Drawer)`
  .MuiDrawer-paper {
    width: min(980px, calc(100vw - 28px));
    border-radius: 18px 0 0 18px;
    border-left: 1px solid var(--cms-border);
    background: var(--cms-dialog-bg);
    box-shadow: -16px 0 40px rgba(15, 23, 42, 0.16);
    padding: 10px 14px 14px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .chart-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 8px 4px 4px;

    .title {
      margin: 0;
      font-size: 18px;
      line-height: 1.2;
      font-weight: 700;
      color: var(--cms-text-primary);
    }
  }

  .chart-content {
    flex: 1;
    min-height: 430px;
    border: 1px solid var(--cms-border);
    border-radius: 12px;
    background: var(--cms-surface-muted);
    padding: 10px;
    overflow: auto;
  }

  .chart-actions {
    display: flex;
    justify-content: flex-end;
    padding: 10px 2px 0;
  }
`

export {
  StyledModernDashboard,
  StyledFilterCard,
  StyledKpiGrid,
  StyledKpiCard,
  StyledInsightGrid,
  StyledInsightPanel,
  StyledInsightList,
  StyledMessageList,
  StyledAgentTrendChart,
  StyledHeatmap,
  StyledDashboardHeading,
  StyledDashboard,
  StyledInfoCard,
  StyledInfoSearchBar,
  StyledChartDrawer,
  StyledLessonEngagementChart,
  StyledEngagementKpiStrip,
  StyledEngagementSearch,
}
